import { atom } from 'jotai';

import {
  addUserToOrganizationMutationAtom,
  removeUserFromOrganizationMutationAtom,
} from '../_graphql/mutationUserOrganization';
import { organizationSearchQueryAtom } from '../_graphql/queryOrganizationSearch';

import { $users } from './userList.state';

// 🔘🔘🔘 TYPES
export type OrganizationAction = {
  id: string; // unique identifier for this action
  type: 'add' | 'remove';
  organizationId: string;
  organizationName: string;
};

export type ManageUserOrganizationsFormData = {
  userId: string;
  userName: string;
  originalOrganizations: Array<{ id: string; name: string }>;
  queuedActions: OrganizationAction[];
};

// 🔘🔘🔘 INITIAL STATE
const getInitialFormData = (): ManageUserOrganizationsFormData => ({
  userId: '',
  userName: '',
  originalOrganizations: [],
  queuedActions: [],
});

// 🔘🔘🔘 PLAIN ATOMS
export const $manageUserOrganizationsDialogOpen = atom(false);
export const $manageUserOrganizationsFormData =
  atom<ManageUserOrganizationsFormData>(getInitialFormData());
export const $isExecutingActions = atom(false);

// 🟡🟡🟡 HIDDEN ACTION -> ADD user to organization
export const $addUserToOrganization = addUserToOrganizationMutationAtom();

// 🟡🟡🟡 HIDDEN ACTION -> REMOVE user from organization
export const $removeUserFromOrganization =
  removeUserFromOrganizationMutationAtom();

// 🟠🟠🟠 ACTION -> OPEN dialog
export const $openManageUserOrganizationsDialog = atom(
  null,
  (
    _get,
    set,
    user: {
      id: string;
      firstName: string;
      lastName: string;
      organizations?: Array<{ id: string; name: string }>;
    },
  ) => {
    set($manageUserOrganizationsFormData, {
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`,
      originalOrganizations: user.organizations ?? [],
      queuedActions: [],
    });
    set($manageUserOrganizationsDialogOpen, true);
  },
);

// 🟠🟠🟠 ACTION -> CLOSE dialog
export const $closeManageUserOrganizationsDialog = atom(null, (_get, set) => {
  set($manageUserOrganizationsDialogOpen, false);
  set($manageUserOrganizationsFormData, getInitialFormData());
});

// 🟠🟠🟠 ACTION -> QUEUE add organization
export const $queueAddOrganization = atom(
  null,
  (get, set, organization: { id: string; name: string }) => {
    const formData = get($manageUserOrganizationsFormData);
    const effectiveOrgs = get($effectiveOrganizations);

    // Don't add if already in effective organizations
    if (effectiveOrgs.some((org) => org.id === organization.id)) {
      return;
    }

    // Remove any existing remove action for this organization
    const filteredActions = formData.queuedActions.filter(
      (action) =>
        !(
          action.type === 'remove' && action.organizationId === organization.id
        ),
    );

    // Add the new add action
    const newAction: OrganizationAction = {
      id: `add-${organization.id}-${Date.now()}`,
      type: 'add',
      organizationId: organization.id,
      organizationName: organization.name,
    };

    set($manageUserOrganizationsFormData, {
      ...formData,
      queuedActions: [...filteredActions, newAction],
    });
  },
);

// 🟠🟠🟠 ACTION -> QUEUE remove organization
export const $queueRemoveOrganization = atom(
  null,
  (get, set, organization: { id: string; name: string }) => {
    const formData = get($manageUserOrganizationsFormData);

    // Remove any existing add action for this organization
    const filteredActions = formData.queuedActions.filter(
      (action) =>
        !(action.type === 'add' && action.organizationId === organization.id),
    );

    // Add the new remove action
    const newAction: OrganizationAction = {
      id: `remove-${organization.id}-${Date.now()}`,
      type: 'remove',
      organizationId: organization.id,
      organizationName: organization.name,
    };

    set($manageUserOrganizationsFormData, {
      ...formData,
      queuedActions: [...filteredActions, newAction],
    });
  },
);

// 🟠🟠🟠 ACTION -> TOGGLE remove organization (for badge clicks)
export const $toggleRemoveOrganization = atom(
  null,
  (get, set, organization: { id: string; name: string }) => {
    const formData = get($manageUserOrganizationsFormData);

    // Check if there's already a remove action for this organization
    const existingRemoveAction = formData.queuedActions.find(
      (action) =>
        action.type === 'remove' && action.organizationId === organization.id,
    );

    if (existingRemoveAction) {
      // Remove the remove action (undo removal)
      const filteredActions = formData.queuedActions.filter(
        (action) => action.id !== existingRemoveAction.id,
      );
      set($manageUserOrganizationsFormData, {
        ...formData,
        queuedActions: filteredActions,
      });
    } else {
      // Add remove action
      set($queueRemoveOrganization, organization);
    }
  },
);

// 🟠🟠🟠 ACTION -> CANCEL queued action
export const $cancelQueuedAction = atom(null, (get, set, actionId: string) => {
  const formData = get($manageUserOrganizationsFormData);
  const filteredActions = formData.queuedActions.filter(
    (action) => action.id !== actionId,
  );

  set($manageUserOrganizationsFormData, {
    ...formData,
    queuedActions: filteredActions,
  });
});

// 🟠🟠🟠 ACTION -> EXECUTE all queued actions
export const $executeQueuedActions = atom(null, async (get, set) => {
  const formData = get($manageUserOrganizationsFormData);

  if (formData.queuedActions.length === 0) {
    return;
  }

  set($isExecutingActions, true);

  try {
    // Sort actions: removals first, then additions
    const removeActions = formData.queuedActions.filter(
      (action) => action.type === 'remove',
    );
    const addActions = formData.queuedActions.filter(
      (action) => action.type === 'add',
    );
    const sortedActions = [...removeActions, ...addActions];

    // Execute actions sequentially
    for (const action of sortedActions) {
      if (action.type === 'remove') {
        await set($removeUserFromOrganization, {
          userId: formData.userId,
          organizationId: action.organizationId,
        });
      } else {
        await set($addUserToOrganization, {
          userId: formData.userId,
          organizationId: action.organizationId,
        });
      }
    }

    // Refresh user list
    get($users).refetch();

    // Close dialog and reset state
    set($manageUserOrganizationsDialogOpen, false);
    set($manageUserOrganizationsFormData, getInitialFormData());
  } finally {
    set($isExecutingActions, false);
  }
});

// 🟢🟢🟢 COMPUTED DATA -> effective organizations (after applying queued actions)
export const $effectiveOrganizations = atom((get) => {
  const formData = get($manageUserOrganizationsFormData);
  const { originalOrganizations, queuedActions } = formData;

  // Start with original organizations
  let effective = [...originalOrganizations];

  // Apply queued actions
  queuedActions.forEach((action) => {
    if (action.type === 'remove') {
      effective = effective.filter((org) => org.id !== action.organizationId);
    } else if (action.type === 'add') {
      // Only add if not already present
      if (!effective.some((org) => org.id === action.organizationId)) {
        effective.push({
          id: action.organizationId,
          name: action.organizationName,
        });
      }
    }
  });

  return effective;
});

// 🟢🟢🟢 COMPUTED DATA -> available organizations for combo (filtered)
export const $availableOrganizations = organizationSearchQueryAtom((_get) => ({
  variables: {
    searchTerm: undefined,
    limit: 50,
  },
}));

export const $availableOrganizationsFiltered = atom((get) => {
  const { data: organizations = [] } = get($availableOrganizations);
  const effectiveOrgs = get($effectiveOrganizations);

  // Filter out organizations already in effective list
  return organizations.filter(
    (org) => !effectiveOrgs.some((effectiveOrg) => effectiveOrg.id === org.id),
  );
});

// 🟢🟢🟢 COMPUTED DATA -> organization states for rendering
export const $organizationStates = atom((get) => {
  const formData = get($manageUserOrganizationsFormData);
  const effectiveOrgs = get($effectiveOrganizations);

  return effectiveOrgs.map((org) => {
    const isOriginal = formData.originalOrganizations.some(
      (originalOrg) => originalOrg.id === org.id,
    );

    const removeAction = formData.queuedActions.find(
      (action) => action.type === 'remove' && action.organizationId === org.id,
    );

    const addAction = formData.queuedActions.find(
      (action) => action.type === 'add' && action.organizationId === org.id,
    );

    let state: 'normal' | 'queued-removal' | 'newly-added';

    if (removeAction) {
      state = 'queued-removal';
    } else if (addAction || !isOriginal) {
      state = 'newly-added';
    } else {
      state = 'normal';
    }

    return {
      ...org,
      state,
      actionId: removeAction?.id || addAction?.id,
    };
  });
});

// 🟢🟢🟢 COMPUTED DATA -> has changes
export const $hasChanges = atom((get) => {
  const formData = get($manageUserOrganizationsFormData);
  return formData.queuedActions.length > 0;
});

// 🟢🟢🟢 COMPUTED DATA -> changes summary
export const $changesSummary = atom((get) => {
  const formData = get($manageUserOrganizationsFormData);
  const addCount = formData.queuedActions.filter(
    (action) => action.type === 'add',
  ).length;
  const removeCount = formData.queuedActions.filter(
    (action) => action.type === 'remove',
  ).length;

  return {
    addCount,
    removeCount,
    totalChanges: addCount + removeCount,
  };
});

// 🟢🟢🟢 COMPUTED DATA -> form validation
export const $manageUserOrganizationsFormValid = atom((get) => {
  const formData = get($manageUserOrganizationsFormData);
  return formData.userId && formData.queuedActions.length > 0;
});

// 🟢🟢🟢 COMPUTED DATA -> is saving
export const $manageUserOrganizationsDialogSaving = atom((get) => {
  const isExecuting = get($isExecutingActions);
  const addStatus = get($addUserToOrganization);
  const removeStatus = get($removeUserFromOrganization);
  return isExecuting || addStatus.isPending || removeStatus.isPending;
});
